import React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import * as fonts from '@helpcenter/styles/src/fonts';
import type { GetRequestCreatePropertyResponse } from 'rest/get-request-create-property';
import { getRequestCreateProperty } from 'rest/get-request-create-property';
import { FORGE_REQUEST_DETAILS, REQUEST_DETAIL_MAIN_SECTION_MODULE } from 'view/common/forge-ui/constants';
import { ExtensionPointProvider } from 'view/common/forge-ui/extension-point-provider';
import { ExtensionProvider } from 'view/common/forge-ui/extension-provider';
import { Renderer } from 'view/common/forge-ui/renderer';
import { withExtensionList } from 'view/common/forge-ui/with-extension-list';
import { token } from '@atlaskit/tokens';
import { useShowBanner } from '@atlassian/help-center-common-util/blocked-apps-banner';
import type {
    Extension,
    ExtensionData,
    WithExtensionListProps,
    WithBlockedAppsBannerProps,
} from 'view/common/forge-ui/types';

interface RequestDetailsMainSectionModuleProps extends WithExtensionListProps {
    portalId: number;
    requestKey: string;
    requestTypeId: number;
}

export const RequestDetailsMainSectionModuleDumb: React.FC<
    RequestDetailsMainSectionModuleProps & WithBlockedAppsBannerProps
> = ({ extensions, portalId, requestKey, requestTypeId, showBanner, resetBannerState }) => {
    const [extensionMap, setExtensionMap] = React.useState<{ [key: string]: { key: string; value: object } }>({});

    const [isAllPromisedSettled, setIsAllPromiseSettled] = React.useState(false);

    const allowedExtensions = useShowBanner(extensions, FORGE_REQUEST_DETAILS, true, showBanner, resetBannerState);

    React.useEffect(() => {
        if (!allowedExtensions.length) return;
        const promises = allowedExtensions.map((extension) => {
            const tokens = extension.id.split('/');
            return getRequestCreateProperty({
                requestKey,
                propertyKey: tokens[1],
                extensionId: extension.id,
            });
        });

        const allSettledPromises = (promisesArg: Promise<GetRequestCreatePropertyResponse>[]) =>
            Promise.all(
                promisesArg.map((promise: Promise<GetRequestCreatePropertyResponse>) =>
                    promise
                        .then((value: GetRequestCreatePropertyResponse) => ({
                            value,
                            status: 'fulfilled',
                        }))
                        .catch((reason) => ({
                            // Suppressing existing violation. Please fix this.
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            reason,
                            value: {
                                extensionId: undefined,
                                key: undefined,
                                value: undefined,
                            },
                            status: 'rejected',
                        }))
                )
            );

        // Disabling existing violations, should be fixed when revisited.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        allSettledPromises(promises).then((respArray) => {
            const tempExtensionMap = {};
            for (const extMap of respArray) {
                if (extMap.status === 'fulfilled' && extMap.value !== undefined) {
                    // @ts-ignore TS(7053) TypeScript upgrade 5.1.6, please fix this violation when you revisit this code.: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                    tempExtensionMap[extMap.value.extensionId ?? ''] = {
                        key: extMap.value.key,
                        value: extMap.value.value,
                    };
                }
            }
            setExtensionMap(tempExtensionMap);
            setIsAllPromiseSettled(true);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extensions, requestKey]);

    const getRequestDetailsMainExtensionData = (extensionId: string): ExtensionData => {
        return {
            type: REQUEST_DETAIL_MAIN_SECTION_MODULE,
            portal: { id: portalId },
            request: {
                typeId: requestTypeId,
                key: requestKey,
                properties: extensionMap[extensionId],
            },
        };
    };

    return (
        <>
            {allowedExtensions.length > 0 && isAllPromisedSettled && (
                <>
                    <Headline>Apps</Headline>
                    <ExtensionPointProvider extensionType={REQUEST_DETAIL_MAIN_SECTION_MODULE}>
                        {allowedExtensions.map((extension: Extension) => (
                            <React.Fragment key={extension.id}>
                                <Headline>{extension.properties.title}</Headline>
                                <ExtensionProvider extension={extension}>
                                    <Renderer
                                        extension={extension}
                                        extensionData={getRequestDetailsMainExtensionData(extension.id)}
                                    />
                                </ExtensionProvider>
                            </React.Fragment>
                        ))}
                    </ExtensionPointProvider>
                </>
            )}
        </>
    );
};

export const RequestDetailsMainSectionModule = withExtensionList(REQUEST_DETAIL_MAIN_SECTION_MODULE)(
    RequestDetailsMainSectionModuleDumb
);

export default RequestDetailsMainSectionModule;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const Headline = styled.h3`
    ${fonts.h400};
    margin-top: ${token('space.300', '24px')};
    margin-bottom: ${token('space.100', '8px')};
`;
